import { Project } from '../../store/Project';
import { InventoryAsset } from '../../types';
import { callExternalApi } from '../callExternalApi';

export interface ProjectsSummaryStats {
  totalProjects: number;
  projectsAtRisk: number;
  // Optional until the API change that adds it is deployed everywhere this portal talks to.
  assigned?: number;
  inProgress: number;
  installed: number;
}

export type RiskTier = 'green' | 'yellow' | 'red';

// Per-tier count breakdown for the Risk & Summary widget. Mutually exclusive buckets
// (an Installed item counts as installed, not under its risk tier) so the tiles sum to the item total.
export interface RiskCounts {
  green: number;
  yellow: number;
  red: number;
  unscored: number;
  installed: number;
}

// How the tier was reached.
export type RiskState = 'scored' | 'resolved' | 'unscored';

// Each item is the full inventory-item payload (same parse as the inventory list) plus risk scoring;
// Partial keeps construction light where only the risk fields matter.
export interface RelatedInventoryRiskItem extends Partial<InventoryAsset> {
  id: string;
  riskTier?: RiskTier | null;
  riskLabel?: string | null;
  riskMessage?: string | null;
  riskState?: RiskState;
}

export interface AtRiskItem {
  assetId: string;
  projectId: string;
  projectName: string | null;
  projectStatus: string | null;
  productType: string | null;
  siteAddress: string | null;
  supplier: string | null;
  nextStep: string | null;
  estimatedDeliveryDate: string | null;
  customerRequestedDate: string | null;
  delayDays: number;
  riskTier: string;
  riskLabel: string;
  riskMessage: string;
}

export interface AtRiskGroup {
  status: string;
  items: AtRiskItem[];
}

export interface AtRiskReport {
  totalProjects: number;
  atRiskCount: number;
  groups: AtRiskGroup[];
}

export const getAtRiskProjects = async (accountIds?: string[]) => {
  if (!accountIds?.length)
    return { data: null, error: { message: 'Must provide account IDs to fetch at-risk projects' } };
  return await callExternalApi<AtRiskReport>({
    url: `api/projects/at_risk?account_ids=${accountIds.join(',')}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const getProjectsSummaryStats = async (accountIds?: string[]) => {
  if (!accountIds?.length)
    return { data: null, error: { message: 'Must provide account IDs to fetch projects summary stats' } };
  return await callExternalApi<ProjectsSummaryStats>({
    url: `api/projects/summary_stats?account_ids=${accountIds.join(',')}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

// Every inventory record under the accounts' projects — the row source for the Projects page's
// Inventory mode. Each item is the same shape the project detail's relatedInventory carries.
export const getProjectsRelatedInventory = async (accountIds?: string[]) => {
  if (!accountIds?.length)
    return { data: null, error: { message: 'Must provide account IDs to fetch project related inventory' } };
  return await callExternalApi<RelatedInventoryRiskItem[]>({
    url: `api/projects/related_inventory?account_ids=${accountIds.join(',')}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const getProjects = async (accountIds?: string[]) => {
  if (!accountIds?.length) return { data: null, error: { message: 'Must provide account IDs to fetch projects' } };
  return await callExternalApi<Project[]>({
    url: `api/projects?account_ids=${accountIds.join(',')}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

// Related inventory costs the api a wide read plus tag and custom-field decoration, so it is only
// attached when asked for — callers that render it (the risk widget, the 1:1 asset view) opt in.
export const getProjectById = async (id: string, includeRelatedInventory = false) => {
  if (!id) return { data: null, error: { message: 'Must provide a project ID to fetch project' } };
  return await callExternalApi<Project>({
    url: `api/projects/${id}${includeRelatedInventory ? '?include_related_inventory=true' : ''}`,
    method: 'GET',
    headers: {
      'content-type': 'application/json'
    }
  });
};

export const updateProjectPortalOptIn = async (id: string, enabled: boolean) => {
  return await callExternalApi<{ managedInClientPortal: boolean }>({
    url: `api/projects/${id}`,
    method: 'PATCH',
    headers: {
      'content-type': 'application/json'
    },
    data: { managed_in_client_portal: enabled }
  });
};
